import { CollectionPopupComponent } from '../../../../shared';
import { voyageSpendErrorMessage } from '../voyage-credits.model';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
import { LocatedVesselRecord, VesselSearchHit } from '../../shared/contracts/vessel.types';
import { countUnnamedVessels, searchLoadedVessels } from '../vessel-search.model';
// The provider's payload dialect, imported rather than injected since phase 9:
// this file already lives in Voyage Radar's folder (D7), so being handed its
// own provider's mapping through a callback was ceremony, not insulation.
import { vrToLocatedRecord } from '../gateways/vr-lookup.gateway';
import { vrHost } from '../vr-host';

/**
 * What this feature must be TOLD rather than know (rule R5 / D1 / phase-5
 * boundary). Each callback is a seam to code still living elsewhere:
 */
export interface SearchFeatureOptions {
  /**
   * A free in-AOI hit was picked — fly to it / select it where possible.
   * Facade glue until the located/lookup machinery finishes its own
   * extraction: the pick spans the live layer, the located store and the
   * selection guard.
   */
  pickLocal: (hit: VesselSearchHit) => void;
  /** A worldwide result was picked — place it, keep it, select it. Same glue. */
  pickWorldwide: (row: any, query: string) => void;
  /** Re-push every price line after a wallet-moving response (the facade's broadcast). */
  syncCreditLines: () => void;
}

/**
 * VESSEL SEARCH — the search drawer's map-side half, as a pluggable feature
 * (phase 5, REFACTOR-PLAN.md §4.3/§7). Extracted verbatim from
 * `VoyageMapController`: the FREE in-memory index over everything loaded this
 * session, the map facts the drawer renders but cannot reach, and the one
 * paid act (the worldwide registry query).
 *
 * The index itself is now `../vessel-search.model` — pure, tested, and Voyage
 * Radar's. The plan once had it becoming SHARED, with AISstream adapting onto
 * this same drawer through a `VesselSearchSource`; that is abandoned. A second
 * provider that wants search writes its own control, its own index and its own
 * descriptor under the same `search` id, distinguished by `operatorKey`. What
 * it reuses is the toolbar host, the drawer chrome and the `VesselSearchHit`
 * shape — not this file's pricing, its precedence rules or its wallet.
 */
export class SearchFeature extends AisMapFeature {
  constructor(ctx: AisMapContext, private readonly opts: SearchFeatureOptions) {
    super(ctx);
  }

  /** What one worldwide search costs on this provider, in its own units. */
  private price(): number {
    return this.ctx.spend?.priceFor('search') ?? 0;
  }

  /**
   * Vessel search. `onSearchLoaded` is the FREE half and the exact analogue of
   * the port-call and nearby `onLookupStored` wires: `searchLoaded` is an
   * in-memory index over the drawn snapshot, every loaded frame and the task's
   * kept vessels, so filtering as you type is a lookup rather than a request.
   * The paid half is `onSearchWorldwide`, which is the only one of the five
   * that spends.
   */
  override attach(): void {
    const search = vrHost(this.ctx.host).vesselSearch;
    search.onSearchLoaded = (q) => this.searchLoaded(q);
    search.onPickLocal = (hit) => this.opts.pickLocal(hit);
    search.onPickWorldwide = (row, q) => this.opts.pickWorldwide(row, q);
    search.onSearchWorldwide = (q) => this.searchWorldwideVessels(q);
  }

  override syncCredits(remaining: number | null, spendingClosed: boolean): void {
    vrHost(this.ctx.host).vesselSearch.setCredits(remaining, this.price(), spendingClosed);
  }

  /**
   * The free in-AOI search index — the rules live in `../vessel-search.model`,
   * this method supplies the LISTS. It stays because the drawer's
   * `onSearchLoaded` wire calls it and because the facade's `snapshotFixFor`
   * reads it.
   *
   * Client-side and instant — no network, no credit, ever.
   *
   * The sub-two-character check is HERE as well as in the model, and that is
   * not a duplicated rule: the model owns the answer (it must return `[]` for
   * any caller), while this guard owns the COST. It runs on every keystroke and
   * the lists below are built eagerly — mapping every loaded frame and
   * normalising the whole kept history — so without it the first character
   * typed would do all of that work to be handed an empty array.
   */
  searchLoaded(query: string, limit = 8): VesselSearchHit[] {
    if (String(query ?? '').trim().length < 2) return [];
    return searchLoadedVessels(
      {
        live: this.ctx.snapshot.liveFeatures,
        frames: this.ctx.snapshot.loadedSteps.map((s) => ({
          time: String(s.time ?? ''),
          vessels: s.features ?? [],
        })),
        // Normalised HERE: the envelope it arrives in (`identity`/`asOf`/
        // `details`) is this provider's API dialect, and the index is written
        // against records rather than payloads.
        kept: this.keptVessels(),
      },
      query,
      limit,
    );
  }

  /** The task's saved vessels, as records. */
  private keptVessels(): LocatedVesselRecord[] {
    const history: any[] = Array.isArray(this.ctx.sensor?.vesselHistory)
      ? this.ctx.sensor.vesselHistory
      : [];
    return history
      .map((raw) => vrToLocatedRecord(raw))
      .filter((r): r is LocatedVesselRecord => !!r);
  }

  /** Saved snapshots exist for this sensor (the two empty maps differ on it). */
  private get hasRefreshHistory(): boolean {
    return (this.ctx.sensor?.refreshHistory?.length ?? 0) > 0;
  }

  /**
   * Push the four map facts the search drawer renders but cannot reach.
   *
   * A toolbar control gets `{ controller, slot, options }` and no sensor; the
   * map machinery is constructed by the host rather than injected, so DI is
   * not a route either. Called from every moment that changes what is drawn —
   * anything that changes the map changes what is searchable.
   */
  pushSearchContext(): void {
    vrHost(this.ctx.host).vesselSearch.setMapContext({
      mapEmpty: !this.ctx.snapshot.dataActive,
      hasHistory: this.hasRefreshHistory,
      vesselCount: this.ctx.snapshot.currentFeatures?.length ?? 0,
      unnamed: countUnnamedVessels(this.ctx.snapshot.liveFeatures),
    });
  }

  /**
   * The one paid act in the search drawer: widen the query to the worldwide
   * registry. Modelled on the port-call fetch, with one deliberate difference.
   *
   * FAILURES REPORT INLINE, not through `popupService`. Every other paid path
   * raises a modal because its drawer has no room for a sentence and the
   * user's next act is elsewhere; a search miss is part of searching, and a
   * modal over a box you are still typing in interrupts the very loop that
   * fixes it. The copy is still the provider's spend-error ladder, so one
   * wallet is never described two ways — only the surface differs.
   *
   * THE PRE-FLIGHT REFUSAL IS THE EXCEPTION TO THAT, and takes the modal every
   * other paid path takes. The inline rule above is scoped to what it was
   * written for — outcomes OF a search, which the box in front of the user is
   * how you fix. A spend refusal is not one of those: it is the same refusal
   * the trace, port-call and nearby drawers raise a modal for, about the same
   * wallet, and the one refusal that reaches here (an ended TOI) cannot be
   * fixed by editing the query at all. Reporting it in the panel's error slot
   * would have been the fourth surface for one sentence, and the quietest.
   */
  private searchWorldwideVessels(q: string): void {
    const sensor = this.ctx.sensor;
    const taskid = this.ctx.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const search = vrHost(this.ctx.host).vesselSearch;
    if (!sensor || !taskid || !sensorId || !q) {
      search.setSearchFailed('This sensor is not ready to search yet.');
      return;
    }

    const refusal = this.ctx.spend ? this.ctx.spend.refuse('search', this.price()) : null;
    if (refusal) {
      // Reset without a message, exactly as the nearby and port-call drawers do
      // before raising their own modal — the panel must not hold a spinner for
      // a request that never left, and must not print the sentence twice.
      search.setSearchFailed();
      this.ctx.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return;
    }

    this.ctx.host.apiService
      .searchVoyageRadarVessels({ taskid, sensorId, q, limit: 20 })
      .toPromise()
      .then((res: any) => {
        const results = Array.isArray(res?.data) ? res.data : [];
        const total =
          Number.isFinite(res?.count) && res.count > results.length ? res.count : null;
        // The stored counter IS the balance — mirror it onto the sensor so the
        // trace, port-call and nearby drawers agree without a refetch.
        if (res?.apiCreditsRemaining != null) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
        }
        search.setWorldwide(q, results, total);
        this.opts.syncCreditLines();
      })
      .catch((err: any) => {
        if (err?.error?.apiCreditsRemaining != null) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
        }
        search.setSearchFailed(
          voyageSpendErrorMessage(err, 'search', this.price()),
        );
        this.opts.syncCreditLines();
      });
  }
}
